import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Grid,
  Paper,
  Stack,
  Typography,
  Skeleton,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import type { SxProps, Theme } from "@mui/material";
import { visuallyHidden } from "@mui/utils";

import strings from "global/constants/StringConstants";
import title from "global/constants/TitleConstants";
import { useTitle } from "utils/UseTitle";
import { hasAccessTo } from "layouts/Config";
import CenteredAuthenticatedLayout from "layouts/CenteredAuthenticated/CenteredAuthenticatedLayout";
import Unauthorized from "screens/Shared/Unauthorized";

import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from "recharts";
import { getRenewableGraphData } from "./RenewableFunction";
import CustomAutoComplete from "global/components/CustomAutoComplete/CustomAutoComplete";
import history from "utils/history";
import viewpaths from "global/constants/ViewPathConstants";
import { RENEWABLE_CONSTANTS } from "./RenewableConstants";
import { useFeatureState } from "hooks/use-feature";
import FeatureKeys from "global/constants/FeatureKeys";
import { centeredPageGutterSx } from "layouts/CenteredAuthenticated/CenteredPage.tokens";
import FeatureGateBlock from "global/components/FeatureGateBlock/FeatureGateBlock";

/**
 * Keyboard/screen-reader equivalent of the bar chart. Kept out of the visual
 * flow (the chart already conveys this to sighted pointer users) but revealed
 * on focus so keyboard users can see where focus is (WCAG 2.4.7).
 */
const chartAlternativeSx: SxProps<Theme> = {
  ...visuallyHidden,
  "&:focus-within": {
    clip: "auto",
    clipPath: "none",
    height: "auto",
    width: "auto",
    // Stays inside the card and scrolls rather than clipping when users
    // enlarge text or override text spacing (WCAG 1.4.4, 1.4.12).
    maxHeight: "calc(100% - 16px)",
    overflow: "auto",
    whiteSpace: "normal",
    margin: 0,
    position: "absolute",
    top: 8,
    left: 8,
    right: 8,
    zIndex: 2,
    p: 2,
    bgcolor: "background.paper",
    border: "1px solid",
    borderColor: "divider",
    borderRadius: 2,
    boxShadow: 4,
  },
};

const RenewableDocuments = () => {
  useTitle(title.RENEWABLE_DOCUMENTS);
  const renewalState = useFeatureState(
    FeatureKeys.ENVELOPE_RENEWAL_NOTIFICATIONS,
  );
  const renewalAvailable =
    renewalState.status === strings.FEATURE_STATUS_AVAILABLE;
  const theme = useTheme();
  const monthOptions = RENEWABLE_CONSTANTS.MONTH_OPTIONS;
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [chartData, setChartData] = useState<any[]>([]);
  const [selectedMonths, setSelectedMonths] = useState<number>(6);

  const hasFetchPermission = hasAccessTo(
    strings.resources.renewable,
    strings.fetchPermission,
  );

  useEffect(() => {
    if (hasFetchPermission && renewalAvailable) {
      getRenewableGraphData(selectedMonths, setIsLoading, setChartData);
    } else {
      /*
        `setLoading` here was redux's action creator, imported but never
        dispatched — a no-op, so a page the plan blocks sat in its skeleton for
        ever and the gate banner below never rendered. `renewalAvailable` also
        has to be a dependency, or the effect never re-runs once features load.
      */
      setIsLoading(false);
    }
  }, [hasFetchPermission, renewalAvailable, selectedMonths]);

  const totalDocuments = chartData.reduce(
    (sum, item) => sum + (Number(item.count) || 0),
    0,
  );

  const chartSummary = chartData.length
    ? `Bar chart of documents due for renewal, covering ${chartData.length} ${
        chartData.length === 1 ? "month" : "months"
      } and ${totalDocuments} ${
        totalDocuments === 1 ? "document" : "documents"
      } in total. Choose a month to see its documents.`
    : "No documents are due for renewal in the selected range.";

  const handleBarClick = (data: any) => {
    if (data && data.originalDate) {
      const clickedDate = data.originalDate;
      history.push({
        pathname: viewpaths.renewableDocumentsListViewPath,
        state: { date: clickedDate },
      });
    }
  };

  const renderHeader = () => (
    <Stack
      direction="row"
      justifyContent="space-between"
      alignItems="center"
      mb={3}
    >
      <Stack spacing={1}>
        <Typography
          variant="h1"
          fontSize="1.6667rem !important"
          color="text.primary"
        >
          Renewable Documents
        </Typography>
        <Box sx={{ maxWidth: { xs: "100%", sm: 320 } }}>
          <CustomAutoComplete
            id="month-filter"
            label="Select duration (including current month)"
            placeHolder="Choose range"
            options={monthOptions}
            getOptionLabel={(option: any) => option.label || ""}
            value={
              monthOptions.find((opt) => opt.value === selectedMonths) || null
            }
            onChange={(event: any, newValue: any) => {
              if (newValue) {
                setSelectedMonths(newValue.value);
              }
            }}
            textFieldVariant="outlined"
            ariaDescribedBy="month-filter-hint"
            {...RENEWABLE_CONSTANTS.MONTH_AUTOCOMPLETE_PROPS}
          />
          <Typography
            id="month-filter-hint"
            variant="caption"
            color="text.secondary"
            display="block"
            mt={0.75}
          >
            Range includes the current month.
          </Typography>
        </Box>
      </Stack>
    </Stack>
  );

  const renderChart = () => (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        border: "1px solid #e0e0e0",
        borderRadius: 2,
        height: isMobile ? 300 : 450,
        display: "flex",
        flexDirection: "column",
        position: "relative",
      }}
    >
      <Typography variant="h6" component="h2" mb={2} textAlign={"right"}>
        Monthly Statistics
      </Typography>
      {/*
        Text + keyboard equivalent of the chart below. The chart itself is a
        pointer-only control (bars are clickable but not focusable), so this
        provides the same data and the same navigation for keyboard and screen
        reader users (WCAG 1.1.1, 2.1.1).
      */}
      <Box sx={chartAlternativeSx}>
        <Typography variant="body2" component="p" mb={1}>
          {chartSummary}
        </Typography>
        {chartData.length > 0 && (
          <Box
            component="ul"
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 1,
              listStyle: "none",
              m: 0,
              p: 0,
            }}
          >
            {chartData.map((entry) => (
              <Box component="li" key={entry.originalDate}>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => handleBarClick(entry)}
                >
                  {`${entry.displayDate}: ${entry.count} ${
                    Number(entry.count) === 1 ? "document" : "documents"
                  }`}
                </Button>
              </Box>
            ))}
          </Box>
        )}
      </Box>
      <Box
        aria-hidden="true"
        sx={{
          width: "100%",
          height: "100%",
          flexGrow: 1,
          "& .recharts-wrapper": {
            outline: "none !important",
          },
          "& .recharts-surface": {
            outline: "none !important",
          },
          "& :focus": {
            outline: "none !important",
          },
        }}
      >
        <ResponsiveContainer>
          <BarChart
            accessibilityLayer={false}
            tabIndex={-1}
            data={chartData}
            margin={{
              top: 10,
              right: 10,
              left: isMobile ? -10 : -20,
              bottom: isMobile ? 20 : 0,
            }}
          >
            <defs>
              <linearGradient
                id="renewableBarGradient"
                x1="0"
                y1="0"
                x2="0"
                y2="1"
              >
                <stop offset="0%" stopColor="#DD3824" stopOpacity={1} />
                <stop offset="100%" stopColor="#FF3E00" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid
              strokeDasharray="4 4"
              vertical={false}
              syncWithTicks={true}
              stroke="#E5E7EB"
            />
            <XAxis
              dataKey="displayDate"
              axisLine={false}
              tickLine={false}
              strokeWidth={1}
              interval={0}
              angle={isMobile ? -30 : 0}
              textAnchor={isMobile ? "end" : "middle"}
              height={isMobile ? 50 : 30}
              tick={{ fontSize: isMobile ? 12 : 16 }}
            />
            <YAxis
              axisLine={false}
              allowDecimals={false}
              tickLine={false}
              strokeWidth={1}
              tick={{ fontSize: isMobile ? 12 : 16 }}
              // Ensure Y-axis tick labels are whole numbers (no decimals).
              tickFormatter={(value) => `${Math.round(Number(value))}`}
            />
            <Tooltip
              cursor={{ fill: "#F3F4F6" }}
              formatter={(value: any) => `${Math.round(Number(value))}`}
              contentStyle={{
                borderRadius: "8px",
                border: "none",
                boxShadow: "0px 4px 12px rgba(0,0,0,0.1)",
              }}
            />
            <Bar
              dataKey="count"
              radius={[isMobile ? 10 : 20, isMobile ? 10 : 20, 0, 0]}
              barSize={isMobile ? 20 : 40}
              onClick={handleBarClick}
              style={{ cursor: "pointer", outline: "none" }}
            >
              {chartData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill="url(#renewableBarGradient)" />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </Box>
    </Paper>
  );

  const renderSkeleton = () => (
    <Stack spacing={2} aria-hidden="true">
      <Skeleton variant="text" width="30%" height={60} />
      <Skeleton variant="text" width="10%" height={20} />
      <Skeleton variant="rounded" width="24%" height={30} />
      <Skeleton
        variant="rectangular"
        width="100%"
        height={450}
        sx={{ borderRadius: 2 }}
      />
    </Stack>
  );

  return (
    <>
      {hasFetchPermission ? (
        <CenteredAuthenticatedLayout>
          {/* The desktop gutter every centred page shares — see
              `CenteredPage.tokens`. `md`-only, so smaller viewports keep the
              layout's own gutters. */}
          <Box sx={centeredPageGutterSx}>
            {/* Announces the loading and the refreshed result set (WCAG 4.1.3). */}
            <Box role="status" aria-live="polite" sx={visuallyHidden}>
              {isLoading
                ? "Loading renewable document statistics."
                : `Statistics updated. ${chartSummary}`}
            </Box>
            {!renewalAvailable ? (
              <FeatureGateBlock
                variant="full"
                featureName="Renewal notifications"
                status={
                  renewalState.status as "not-in-plan" | "admin-disabled"
                }
                disabledMessage={(renewalState as any).disabledMessage}
              />
            ) : isLoading ? (
              renderSkeleton()
            ) : (
              <>
                {renderHeader()}
                <Grid container spacing={3} sx={{ mt: 0 }}>
                  <Grid item xs={12}>
                    {renderChart()}
                  </Grid>
                </Grid>
              </>
            )}
          </Box>
        </CenteredAuthenticatedLayout>
      ) : (
        <Unauthorized permissionDenied={!hasFetchPermission} />
      )}
    </>
  );
};

export default RenewableDocuments;
